import React, { useEffect, useState } from 'react';
import { Form, Button, Modal, Container, Row, Col, Card, InputGroup } from 'react-bootstrap';
import { FaCheck, FaPlus } from 'react-icons/fa';
import './CreatePage.css';
import Editor from '../../../Utilities/Editor';
import axios from '../../../interseptor/axiosConf';
import { useLocation, useNavigate } from 'react-router-dom';
import ToastrNotifi from '../../../Utilities/ToastrNotifi';
import { useBreadcrumbs } from '../../../Utilities/Breadscrumb/BreadcrumbContext';
import { useAddTag, useTagsData } from '../../../hooks/useTagsData';
import { useAddPage } from '../../../hooks/usePageData';
import GlobalSpinner from '../../../Utilities/GlobleSpinner';

const CreatePage = () => {
  const { data: tagsData, isSuccess: tagDataIsSuccess, refetch: tagsRefetch, isLoading: isTagsLoading } = useTagsData();
  const { mutate: addTag } = useAddTag();
  const { mutate: addPage } = useAddPage();

  const [pageName, setPageName] = useState('');
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newTag, setNewTag] = useState('');
  const beURL = localStorage.getItem('BeURL');
  const [editorContent, setEditorContent] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { shelve_id, shelve_name } = location.state || {};
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (tagDataIsSuccess) {
      setTags(tagsData);
    }
  }, [tagDataIsSuccess, tagsData]);

  const handleCategoryClick = (category) => {
    setSelectedTags((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const handleAddCategory = () => {
    const tag_data = { tagname: newTag };
    addTag(tag_data, {
      onSuccess: () => {
        tagsRefetch();
        setNewTag('');
        setShowModal(false);
      },
      onError: () => ToastrNotifi.errorToast("Failed to add tag"),
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const requestData = {
      pageName,
      selectedTags,
      editorContent,
      shelve_id,
      shelve_name,
    };

    // Reset errors before submitting
    setErrors({});

    addPage(requestData, {
      onError: (error) => {
        if (error.response && error.response.data && error.response.data.errors) {
          setErrors(error.response.data.errors); // Set backend errors in state
          const errorMessages = Object.values(error.response.data.errors).flat();
          errorMessages.forEach(message => ToastrNotifi.warningToast(message));
        } else {
          console.error('An unexpected error occurred:', error);
          ToastrNotifi.errorToast("An unexpected error occurred. Please try again.");
        }
      },
      onSuccess: (response) => {
        // Optionally reset form fields or navigate to another page
        ToastrNotifi.successToast("Page created successfully!");
        navigate(`/page/content/${response.page.id}/${response.page.slug}`);
      },
    });
  };


  const { addBreadcrumb } = useBreadcrumbs();
  useEffect(() => {
    if (shelve_name) addBreadcrumb(`Create New Page to ${shelve_name}`, location.pathname);
  }, [addBreadcrumb, location.pathname, shelve_name]);

  const findTags = (query) => {
    const regex = new RegExp(query, 'i');
    setTags(query ? tagsData.filter(tag => regex.test(tag.name)) : tagsData);
  };

  const sortedTags = [...tags].sort((a, b) => {
    const aSelected = selectedTags.includes(a.id);
    const bSelected = selectedTags.includes(b.id);
    return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
  });

  return (
    <Container fluid>
      {!isTagsLoading ? (
        <Row className="justify-content-center">
          <Col md={12}>
            <Card className="p-4 shadow-sm">
              <h3 className="mb-4">Create New Page to {shelve_name}</h3>
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="pageName" className="mb-3">
                  <Form.Label>Page Name
                  {errors.pageName?(
                          <div className="text-danger error-text">**{errors.pageName}</div>
                        ):(null)}
                  </Form.Label>
                 
                  <Form.Control
                    type="text"
                    placeholder="Enter page name"
                    value={pageName}
                    onChange={(e) => setPageName(e.target.value)}
                    required
                  />
                </Form.Group>

                <Card>
                  <Card.Body>
                    <Form.Group controlId="categoryList" className="mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Label>Tags
                        {errors.selectedTags?(
                          <div className="text-danger error-text">**{errors.selectedTags}</div>
                        ):(null)}
                        </Form.Label>
                       
                        <Button
                          variant="outline-secondary btn-sm"
                          onClick={() => setShowModal(true)}
                          style={{ fontSize: '10px' }}
                        >
                          <FaPlus /> Add New Tag
                        </Button>
                      </div>

                      <Form.Group controlId="searchCategory" className="mb-3">
                        <Col sm={12} md={3}>
                          <InputGroup className="input-group-sm">
                            <Form.Control
                              type="text"
                              placeholder="Search tags..."
                              onChange={(e) => findTags(e.target.value)}
                            />
                          </InputGroup>
                        </Col>
                      </Form.Group>

                      <div className="scrollable-container">
                        <div className="tag-list">
                          {sortedTags.length===0 ?(
                            <p>
                              No Tags Found!
                            </p>
                          ):(
                            <>
                            {sortedTags.map((tag) => (
                            <Button
                              key={tag.id}
                              variant={selectedTags.includes(tag.id) ? 'primary' : 'outline-secondary'}
                              className="me-2 mb-2 btn-sm"
                              onClick={() => handleCategoryClick(tag.id)}
                            >
                              {selectedTags.includes(tag.id) ? (
                                <FaCheck className="mx-1" />
                              ) : (
                                <FaPlus className="mx-1" />
                              )}
                              {tag.name}
                            </Button>
                          ))}
                            </>
                          )}
                          
                        </div>
                      </div>
                    </Form.Group>
                  </Card.Body>
                </Card>

                <Form.Group className="mb-3">
                  <Form.Label>Page Content
                  {errors.editorContent?(
                          <div className="text-danger error-text">**{errors.editorContent}</div>
                        ):(null)}
                  </Form.Label>
                  <Editor editorContent={editorContent} setEditorContent={setEditorContent} />
                </Form.Group>

                <div className="text-center">
                  <Button variant="primary" type="submit" className="px-4">
                    Create Page
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
      ) : <GlobalSpinner/>}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="newTag">
            <Form.Label>Tag Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter new tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAddCategory}>
            Add Tag
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CreatePage;
